// Zentrale SEO-Metadaten pro Seite und Sprache.
// Single Source of Truth für <title> und <meta description> – wird von der
// <Seo>-Komponente (src/components/Seo.tsx) gelesen. Die Routen-Pfade dazu
// liegen in src/seo/routes.json (ebenfalls Quelle für die Sitemap-Generierung).

export type Lang = 'de' | 'en' | 'es' | 'ru';

export interface MetaEntry {
  title: string;
  description: string;
}

// Reihenfolge, in der bei fehlender Sprache auf eine vorhandene zurückgegriffen wird.
export const FALLBACK_ORDER: Lang[] = ['es', 'de', 'en'];

// og:locale pro Sprache.
export const OG_LOCALE: Record<Lang, string> = {
  de: 'de_DE',
  en: 'en_GB',
  es: 'es_ES',
  ru: 'ru_RU',
};

export const seoMeta: Record<string, Partial<Record<Lang, MetaEntry>>> = {
  home: {
    de: {
      title: 'SeaDent Mallorca – Moderne Zahnmedizin in Palma',
      description: 'SeaDent Mallorca bietet moderne Zahnmedizin in Palma. Entdecken Sie unsere Wohlfühlerfahrung und umfassenden Zahnbehandlungen in mediterranem Ambiente.',
    },
    en: {
      title: 'SeaDent Mallorca – Modern Dentistry in Palma',
      description: 'SeaDent Mallorca offers modern dentistry in Palma. Discover our comfort experience and comprehensive dental treatments in a Mediterranean setting.',
    },
    es: {
      title: 'SeaDent Mallorca – Odontología moderna en Palma',
      description: 'SeaDent Mallorca ofrece odontología moderna en Palma. Descubre nuestra experiencia de confort y tratamientos dentales completos en un ambiente mediterráneo.',
    },
    ru: {
      title: 'SeaDent Майорка – Современная стоматология в Пальме',
      description: 'SeaDent Майорка предлагает современную стоматологию в Пальме. Откройте для себя атмосферу комфорта и полный спектр стоматологических услуг.',
    },
  },
  implants: {
    de: {
      title: 'Implantate – SeaDent Mallorca',
      description: 'Moderne Zahnimplantate für ein natürliches Lächeln und mehr Lebensqualität. Hochwertige Materialien und schmerzfreie Behandlung bei SeaDent Mallorca.',
    },
    en: {
      title: 'Dental Implants – SeaDent Mallorca',
      description: 'Modern dental implants for a natural smile and improved quality of life. High-quality materials and pain-free treatment at SeaDent Mallorca.',
    },
    es: {
      title: 'Implantes Dentales – SeaDent Mallorca',
      description: 'Implantes dentales modernos para una sonrisa natural y mejor calidad de vida. Materiales de alta calidad y tratamiento sin dolor en SeaDent Mallorca.',
    },
    ru: {
      title: 'Зубные Имплантаты – SeaDent Майорка',
      description: 'Современные зубные имплантаты для естественной улыбки и улучшения качества жизни. Высококачественные материалы и безболезненное лечение в SeaDent Майорка.',
    },
  },
  composite: {
    de: {
      title: 'Komposit-Zahnaufbau – SeaDent Mallorca',
      description: 'Komposit-Zahnaufbau bei SeaDent Mallorca: direkt, additiv, ohne Beschleifen – Ihr neues Lächeln in einer Sitzung mit Freihand-Schichttechnik.',
    },
    en: {
      title: 'Composite Build-up – SeaDent Mallorca',
      description: 'Composite build-up at SeaDent Mallorca: direct, additive, no drilling – your new smile in a single visit using freehand layering technique.',
    },
    es: {
      title: 'Reconstrucción con Composite – SeaDent Mallorca',
      description: 'Reconstrucción con composite en SeaDent Mallorca: directa, aditiva, sin tallar – tu nueva sonrisa en una sola sesión con técnica de estratificación a mano alzada.',
    },
    ru: {
      title: 'Композитная реставрация – SeaDent Майорка',
      description: 'Композитная реставрация в SeaDent Майорка: прямая, аддитивная, без обточки – новая улыбка за один визит, послойная техника от руки.',
    },
  },
  veneers: {
    de: {
      title: 'Veneers – SeaDent Mallorca',
      description: 'Dünne Keramikschalen für ästhetische Korrekturen an Ihren Zähnen. Erleben Sie ein harmonisches, natürliches Erscheinungsbild bei SeaDent Mallorca.',
    },
    en: {
      title: 'Veneers – SeaDent Mallorca',
      description: 'Thin ceramic shells for aesthetic corrections to your teeth. Experience a harmonious, natural appearance at SeaDent Mallorca.',
    },
    es: {
      title: 'Carillas – SeaDent Mallorca',
      description: 'Finas carillas de cerámica para correcciones estéticas en sus dientes. Experimente una apariencia armoniosa y natural en SeaDent Mallorca.',
    },
    ru: {
      title: 'Виниры – SeaDent Майорка',
      description: 'Тонкие керамические накладки для эстетической коррекции ваших зубов. Получите гармоничный, естественный вид в SeaDent Майорка.',
    },
  },
  technologies: {
    de: {
      title: 'Technologien – SeaDent Mallorca',
      description: 'Modernste Technologien für Ihre Zahnbehandlung: DVT, Intraoralscanner und Dentallaser. Schmerzfrei und komfortabel bei SeaDent Mallorca.',
    },
    en: {
      title: 'Technologies – SeaDent Mallorca',
      description: 'State-of-the-art technologies for your dental treatment: DVT, intraoral scanner, and dental laser. Pain-free and comfortable at SeaDent Mallorca.',
    },
    es: {
      title: 'Tecnologías – SeaDent Mallorca',
      description: 'Tecnologías de vanguardia para su tratamiento dental: DVT, escáner intraoral y láser dental. Sin dolor y cómodo en SeaDent Mallorca.',
    },
    ru: {
      title: 'Технологии – SeaDent Майорка',
      description: 'Передовые технологии для вашего стоматологического лечения: КТ, интраоральный сканер и стоматологический лазер. Безболезненно и комфортно в SeaDent Майорка.',
    },
  },
  emergency: {
    de: {
      title: 'Zahnärztlicher Notdienst 24/7 – SeaDent Mallorca',
      description: 'Zahnarzt-Notdienst in Palma de Mallorca: Notfall-Telefon 24/7, schnelle Termine, Soforthilfe bei Zahnschmerzen, Zahnunfall und Abszess. Tel: 603 31 96 48',
    },
    en: {
      title: 'Emergency Dentist Palma de Mallorca — 24/7 | SeaDent',
      description: 'Emergency dentist in Palma de Mallorca: 24/7 emergency phone, fast appointments, immediate help for toothache, broken teeth and abscess. Call: 603 31 96 48',
    },
    es: {
      title: 'Servicio de Urgencias Dentales 24/7 – SeaDent Mallorca',
      description: 'Dentista de urgencias en Palma de Mallorca: teléfono de urgencias 24/7, citas rápidas, ayuda inmediata para dolor de muelas, dientes rotos y flemones. Tel: 603 31 96 48',
    },
    ru: {
      title: 'Экстренная стоматологическая помощь в Пальме | SeaDent',
      description: 'Срочная стоматологическая помощь в Пальма-де-Майорка: телефон 24/7, быстрый приём при острой зубной боли, сломанном зубе и флюсе. Тел: 603 31 96 48',
    },
  },
  'emergency-broken-tooth': {
    de: {
      title: 'Zahn abgebrochen auf Mallorca? Soforthilfe in Palma | SeaDent',
      description: 'Zahn abgebrochen oder ausgeschlagen? Erste Hilfe, 60-Minuten-Regel und Composite-Aufbau in einer Sitzung – Zahnarzt-Notfall in Palma de Mallorca. Tel: 603 31 96 48',
    },
    en: {
      title: 'Broken Tooth in Mallorca? Emergency Help in Palma | SeaDent',
      description: 'Broken or knocked-out tooth? First aid, the 60-minute rule and same-day composite repair – emergency dentist in Palma de Mallorca. Call: 603 31 96 48',
    },
    es: {
      title: '¿Diente roto en Mallorca? Ayuda urgente en Palma | SeaDent',
      description: '¿Diente roto o caído por un golpe? Primeros auxilios, la regla de los 60 minutos y reconstrucción con composite en una sesión – dentista de urgencias en Palma. Tel: 603 31 96 48',
    },
    ru: {
      title: 'Сломался зуб на Майорке? Срочная помощь в Пальме | SeaDent',
      description: 'Сломался или выпал зуб? Первая помощь, правило 60 минут и восстановление композитом за один визит – стоматолог в Пальме. Тел: 603 31 96 48',
    },
  },
  'emergency-toothache': {
    de: {
      title: 'Akute Zahnschmerzen auf Mallorca? Sofort-Termin in Palma | SeaDent',
      description: 'Akute Zahnschmerzen – auch nachts? Rückruf in unter 30 Minuten, Notfall-Wurzelbehandlung unter dem Mikroskop, Schmerzstopp am selben Tag. Tel: 603 31 96 48',
    },
    en: {
      title: 'Acute Toothache in Mallorca? Same-Day Appointment | SeaDent',
      description: 'Severe toothache – even at night? Call-back within 30 minutes, emergency root canal treatment under the microscope, pain relief the same day. Call: 603 31 96 48',
    },
    es: {
      title: '¿Dolor de muelas insoportable? Cita urgente en Palma | SeaDent',
      description: 'Dolor de muelas agudo, ¿también de noche? Le devolvemos la llamada en menos de 30 minutos, endodoncia de urgencia con microscopio, alivio el mismo día. Tel: 603 31 96 48',
    },
    ru: {
      title: 'Острая зубная боль на Майорке? Срочный приём в Пальме | SeaDent',
      description: 'Острая зубная боль, в том числе ночью? Перезвоним в течение 30 минут, экстренное лечение каналов под микроскопом, облегчение в тот же день. Тел: 603 31 96 48',
    },
  },
  'emergency-tourists': {
    de: {
      title: 'Zahnnotfall im Urlaub auf Mallorca – Ihr Fahrplan | SeaDent',
      description: 'Zahnnotfall im Mallorca-Urlaub: Schritt-für-Schritt-Fahrplan, Kosten und Versicherung, mehrsprachiges Team (DE/EN/ES), zentrale Lage in Palma. Tel: 603 31 96 48',
    },
    en: {
      title: 'Dental Emergency on Holiday in Mallorca – Your Guide | SeaDent',
      description: 'Dental emergency on your Mallorca holiday: step-by-step guide, costs and insurance, multilingual team (EN/DE/ES), central location in Palma. Call: 603 31 96 48',
    },
    es: {
      title: 'Urgencia dental para visitantes en Palma | SeaDent',
      description: 'Urgencia dental durante su estancia en Mallorca: guía paso a paso, costes y formas de pago, equipo multilingüe, en pleno centro de Palma. Tel: 603 31 96 48',
    },
    ru: {
      title: 'Зубная боль в отпуске на Майорке – что делать | SeaDent',
      description: 'Стоматологическая помощь туристам на Майорке: пошаговый план, цены и документы для страховой, приём на английском, немецком и испанском, центр Пальмы. Тел: 603 31 96 48',
    },
  },
  'emergency-abscess': {
    de: {
      title: 'Zahnabszess oder dicke Backe? Heute handeln – Palma | SeaDent',
      description: 'Zahnabszess oder geschwollene Wange? Warum Abwarten gefährlich ist, was Sie jetzt tun können und wie wir helfen: Drainage, Mikroskop-Wurzelbehandlung, Laser. Tel: 603 31 96 48',
    },
    en: {
      title: 'Dental Abscess or Swollen Cheek? Act Today – Palma | SeaDent',
      description: 'Dental abscess or facial swelling? Why waiting is dangerous, what to do now and how we help: drainage, microscope root canal, dental laser. Call: 603 31 96 48',
    },
    es: {
      title: '¿Flemón dental o mejilla hinchada? Actúe hoy – Palma | SeaDent',
      description: '¿Flemón (absceso dental) o hinchazón en la cara? Por qué esperar es peligroso, qué hacer ahora y cómo ayudamos: drenaje, endodoncia con microscopio, láser. Tel: 603 31 96 48',
    },
    ru: {
      title: 'Флюс или опухла щека? Действуйте сегодня – Пальма | SeaDent',
      description: 'Флюс (абсцесс зуба) или отёк щеки? Почему нельзя ждать, что делать сейчас и как мы помогаем: дренаж, лечение каналов под микроскопом, лазер. Тел: 603 31 96 48',
    },
  },
  contact: {
    de: {
      title: 'Kontakt – SeaDent Mallorca',
      description: 'Kontaktieren Sie SeaDent Mallorca in Palma. Zahnarztpraxis unter Leitung von Dra. Svetlana Buch. Telefon, Adresse und Anfahrt.',
    },
    en: {
      title: 'Contact – SeaDent Mallorca',
      description: 'Contact SeaDent Mallorca in Palma. Dental practice led by Dr. Svetlana Buch. Phone, address and directions.',
    },
    es: {
      title: 'Contacto – SeaDent Mallorca',
      description: 'Contacte con SeaDent Mallorca en Palma. Clínica dental dirigida por la Dra. Svetlana Buch. Teléfono, dirección e indicaciones.',
    },
    ru: {
      title: 'Контакт – SeaDent Майорка',
      description: 'Свяжитесь с SeaDent Майорка в Пальме. Стоматологическая клиника под руководством доктора Светланы Бух. Телефон, адрес и как добраться.',
    },
  },
  privacy: {
    de: {
      title: 'Datenschutz – SeaDent Mallorca',
      description: 'Datenschutzerklärung von SeaDent Mallorca: So erheben, verarbeiten und schützen wir Ihre personenbezogenen Daten gemäß DSGVO.',
    },
    en: {
      title: 'Privacy Policy – SeaDent Mallorca',
      description: 'Privacy policy of SeaDent Mallorca: how we collect, process and protect your personal data in accordance with the GDPR.',
    },
    es: {
      title: 'Privacidad – SeaDent Mallorca',
      description: 'Política de privacidad de SeaDent Mallorca: cómo recopilamos, tratamos y protegemos sus datos personales conforme al RGPD.',
    },
    ru: {
      title: 'Конфиденциальность – SeaDent Майорка',
      description: 'Политика конфиденциальности SeaDent Майорка: как мы собираем, обрабатываем и защищаем ваши персональные данные в соответствии с GDPR.',
    },
  },
  legal: {
    de: {
      title: 'Impressum – SeaDent Mallorca',
      description: 'Impressum und rechtliche Angaben von SeaDent Mallorca, Palma de Mallorca.',
    },
    en: {
      title: 'Legal Notice – SeaDent Mallorca',
      description: 'Legal notice and company information for SeaDent Mallorca, Palma de Mallorca.',
    },
    es: {
      title: 'Aviso Legal – SeaDent Mallorca',
      description: 'Aviso legal e información de la empresa de SeaDent Mallorca, Palma de Mallorca.',
    },
    ru: {
      title: 'Правовая информация – SeaDent Майорка',
      description: 'Правовая информация и сведения о компании SeaDent Майорка, Пальма-де-Майорка.',
    },
  },
};

/**
 * Liefert den passenden Meta-Eintrag für Seite + Sprache, mit Fallback-Kette.
 */
export function resolveMeta(pageKey: string, language: string): MetaEntry | undefined {
  const entry = seoMeta[pageKey];
  if (!entry) return undefined;
  const lang = language as Lang;
  if (entry[lang]) return entry[lang];
  for (const fb of FALLBACK_ORDER) {
    if (entry[fb]) return entry[fb];
  }
  return undefined;
}
